import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import Select from "react-select";
import API from "../../Network/API";
import { ADD_JOB_FETCH_SUCCESS, EDIT_JOB_FETCH_SUCCESS } from "../../Reducer/JobSlice";

const emptyForm = () => ({
    subject: "",
    details: "",
    phone: "",
    card_number: "",
    user_name: "",
    type: "4",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
});

export default function JobFormModal({ mode = "create", jobId = 0, isModalVisible, handleShowB }) {
    const modalRef = useRef();
    const dispatch = useDispatch();
    const isEdit = mode === "edit";

    const [form, setForm] = useState(emptyForm);
    const [options, setOptions] = useState([]);
    const [options1, setOptions1] = useState([]);
    const [options2, setOptions2] = useState([]);
    const [options3, setOptions3] = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [selectedSiteValues, setSelectedSiteValues] = useState([]);
    const [organizationId, setOrganizationId] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("");
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [selectedZone, setSelectedZone] = useState(null);
    const [customUser, setCustomUser] = useState(false);
    const [clusterId, setClusterId] = useState("");
    const [zoneId, setZoneId] = useState("");
    const [loading, setLoading] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [formReady, setFormReady] = useState(false);

    const resetForm = () => {
        setForm(emptyForm());
        setOptions2([]);
        setOptions3([]);
        setSiteOptions([]);
        setSelectedSiteValues([]);
        setOrganizationId("");
        setSelectedUserId("");
        setSelectedCluster(null);
        setSelectedZone(null);
        setCustomUser(false);
        setClusterId("");
        setZoneId("");
        setIsChecked(false);
        setSiteSearch("");
        setApiSuggestions([]);
        setSitePage(1);
    };

    const loadVendorUsers = (orgId, userId = "") =>
        API.get(`/v1/oauth/ajax_vendor_users/${orgId}`).then((response) => {
            const users = response.data?.data?.users ?? [];
            setOptions3(users);
            if (userId) {
                setSelectedUserId(String(userId));
            }
            return users;
        });

    const applyJobToForm = async (job) => {
        const sites = job.sites_selected ?? [];
        setForm({
            subject: job.subject ?? "",
            details: job.details ?? "",
            phone: job.phone ?? job.phone_number ?? "",
            card_number: job.card_number ?? "",
            user_name: job.user_name ?? "",
            type: String(job.type ?? 4),
            start_date: job.start_date_input ?? "",
            start_time: job.start_time_input ?? "",
            end_date: job.end_date_input ?? "",
            end_time: job.end_time_input ?? "",
        });
        setCustomUser(String(job.user_type) === "0");
        setIsChecked(!!job.job_validity_unlimited);
        setOrganizationId(job.organization_id ? String(job.organization_id) : "");
        setSelectedSiteValues(sites);
        setSiteOptions(sites);
        setSelectedUserId(job.user_id ? String(job.user_id) : "");

        if (job.organization_id) {
            await loadVendorUsers(job.organization_id, job.user_id);
        }
    };

    useEffect(() => {
        if (!isModalVisible) {
            setFormReady(false);
            return;
        }

        resetForm();
        setFormReady(false);

        if (isEdit) {
            if (!jobId) return;
            API.get(`/v1/oauth/jobs/${jobId}/edit`)
                .then(async (response) => {
                    const payload = response.data?.data ?? {};
                    setOptions(payload.clusters ?? []);
                    setOptions1(payload.organizations ?? []);
                    if (payload.job) {
                        await applyJobToForm(payload.job);
                    }
                    setFormReady(true);
                })
                .catch((error) => {
                    toast.error(error.response?.data?.message ?? "Failed to load job");
                });
            return;
        }

        API.get("/v1/oauth/jobs/create")
            .then((response) => {
                setOptions(response.data?.data?.clusters ?? []);
                setOptions1(response.data?.data?.organizations ?? []);
                setFormReady(true);
            })
            .catch((error) => {
                toast.error(error.response?.data?.message ?? "Failed to load form");
            });
    }, [isModalVisible, isEdit, jobId]);

    const searchZone = (e) => {
        setOptions2([]);
        setSelectedZone(null);
        setZoneId("");
        setSelectedCluster(e);
        setClusterId(e ? e.value : "");
        if (!e) return;
        API.get(`/v1/oauth/ajax_cluster_zone/${e.value}`).then((response) => {
            setOptions2(response.data?.data?.zone ?? []);
        });
    };

    const searchZoneSite = (e) => {
        setSelectedZone(e);
        setZoneId(e ? e.value : "");
    };

    const [siteSearch, setSiteSearch] = useState("");
    const [apiSuggestions, setApiSuggestions] = useState([]);
    const [searchingApi, setSearchingApi] = useState(false);
    const [sitePage, setSitePage] = useState(1);
    const SITE_PAGE_SIZE = 8;

    const siteSearchTimer = useRef(null);
    const siteSearchAbort = useRef(null);

    const filteredSelectedSites = useMemo(() => {
        const q = siteSearch.trim().toLowerCase();
        if (!q) return selectedSiteValues;
        return selectedSiteValues.filter((s) =>
            String(s.label ?? s.value).toLowerCase().includes(q)
        );
    }, [siteSearch, selectedSiteValues]);

    const paginatedSites = useMemo(() => {
        const start = (sitePage - 1) * SITE_PAGE_SIZE;
        return filteredSelectedSites.slice(start, start + SITE_PAGE_SIZE);
    }, [filteredSelectedSites, sitePage]);

    const totalPages = Math.max(1, Math.ceil(filteredSelectedSites.length / SITE_PAGE_SIZE));

    useEffect(() => {
        if (sitePage > totalPages) setSitePage(1);
    }, [totalPages, sitePage]);

    const callSiteApi = (q) => {
        if (siteSearchAbort.current) siteSearchAbort.current.abort();
        const controller = new AbortController();
        siteSearchAbort.current = controller;
        setSearchingApi(true);
        API.get(`/v1/oauth/ajax_sites?q=${encodeURIComponent(q)}&zone_id=${zoneId}&limit=50`, {
            signal: controller.signal,
        })
            .then((response) => {
                const sites = response.data?.data?.sites ?? [];
                const selectedIds = new Set(selectedSiteValues.map((s) => String(s.value)));
                setApiSuggestions(sites.filter((s) => !selectedIds.has(String(s.value))));
            })
            .catch(() => setApiSuggestions([]))
            .finally(() => setSearchingApi(false));
    };

    const handleSiteSearch = (value) => {
        setSiteSearch(value);
        setSitePage(1);
        setApiSuggestions([]);

        if (siteSearchTimer.current) clearTimeout(siteSearchTimer.current);
        const q = value.trim();
        if (q.length < 2) return;

        const localHit = selectedSiteValues.some((s) =>
            String(s.label ?? s.value).toLowerCase().includes(q.toLowerCase())
        );
        if (localHit) return;

        siteSearchTimer.current = setTimeout(() => callSiteApi(q), 300);
    };

    const addSite = (site) => {
        if (!site) return;
        setSelectedSiteValues((prev) => {
            if (prev.some((s) => String(s.value) === String(site.value))) return prev;
            return [...prev, site];
        });
        setApiSuggestions([]);
        setSiteSearch("");
        if (siteSearchAbort.current) siteSearchAbort.current.abort();
    };

    const removeSite = (siteValue) => {
        setSelectedSiteValues((prev) => prev.filter((s) => String(s.value) !== String(siteValue)));
    };

    const clearAllSites = () => {
        setSelectedSiteValues([]);
        setSiteSearch("");
        setApiSuggestions([]);
        setSitePage(1);
    };

    const searchUser = (e) => {
        setOptions3([]);
        setSelectedUserId("");
        if (!e) {
            setOrganizationId("");
            return;
        }
        setOrganizationId(String(e.value));
        loadVendorUsers(e.value);
    };

    const ajaxUserInfo = (e) => {
        if (!e) {
            setSelectedUserId("");
            setForm((prev) => ({ ...prev, phone: "", card_number: "", user_name: "" }));
            return;
        }
        setSelectedUserId(String(e.value));
        API.get(`/v1/oauth/ajax_user_info?user_id=${e.value}`).then((response) => {
            const user = response.data?.data?.user;
            if (!user) return;
            setForm((prev) => ({
                ...prev,
                phone: user.phone ?? prev.phone,
                card_number: user.card_number ?? prev.card_number,
                user_name: user.name ?? prev.user_name,
            }));
        });
    };

    const buildFormData = () => {
        const site = selectedSiteValues.map((opt) => opt.value);
        const fd = new FormData();
        fd.append("start_date", form.start_date);
        fd.append("start_time", form.start_time);
        if (form.end_time) {
            fd.append("end_time", form.end_time);
            fd.append("end_date", form.end_date);
        }
        if (isChecked) {
            fd.append("job_validity_type", "1");
        }
        const userType = customUser ? "0" : "1";
        if (userType === "1") {
            fd.append("user_id", selectedUserId);
        }
        fd.append("subject", form.subject);
        fd.append("details", form.details);
        fd.append("card_number", form.card_number);
        fd.append("phone", form.phone);
        fd.append("site_id", site.join(","));
        fd.append("type", form.type);
        fd.append("user_type", userType);
        fd.append("cluster_id", clusterId);
        fd.append("zone_id", zoneId);
        fd.append("organization_id", organizationId);
        fd.append("user_name", form.user_name);
        return fd;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const userType = customUser ? "0" : "1";
        if (userType === "1" && !selectedUserId) {
            toast.error("Please select a user");
            return;
        }
        if (!organizationId) {
            toast.error("Please select an organization");
            return;
        }
        if (form.type === "4" && selectedSiteValues.length === 0) {
            toast.error("Please select at least one site");
            return;
        }

        setLoading(true);
        const fd = buildFormData();
        const request = isEdit
            ? API.post(`/v1/oauth/jobs/${jobId}/update`, fd)
            : API.post("/v1/oauth/jobs/store", fd);

        request
            .then((response) => {
                setLoading(false);
                toast.success(response.data?.message ?? "Saved");
                if (isEdit) {
                    dispatch(EDIT_JOB_FETCH_SUCCESS(response.data.data));
                } else {
                    dispatch(ADD_JOB_FETCH_SUCCESS(response.data.data));
                }
                handleShowB();
            })
            .catch((error) => {
                setLoading(false);
                const message = error.response?.data?.message
                    ?? error.response?.data?.errors
                    ?? "Request failed";
                toast.error(typeof message === "string" ? message : "Validation failed");
            });
    };

    const checkUserType = (e) => {
        const isCustom = e.target.value === "0";
        setCustomUser(isCustom);
        if (isCustom) {
            setSelectedUserId("");
        }
    };

    const updateField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <Modal
            isOpen={isModalVisible}
            toggle={handleShowB}
            className="modal-dialog-scrollable modal-lg"
            backdrop="static"
            ref={modalRef}
        >
            <ModalHeader toggle={handleShowB}>
                {isEdit ? "Edit Job" : "Add Job"}
            </ModalHeader>
            <ModalBody className="bg-white">
                {!formReady ? (
                    <div className="text-center py-4">Loading...</div>
                ) : (
                    <div className="meeting-schedule-popup">
                        <form onSubmit={handleSubmit} id="job_form_modal">
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="form-group col-md-6">
                                            <label className="control-label">Type</label>
                                            <select
                                                className="form-select"
                                                name="type"
                                                value={form.type}
                                                onChange={(e) => updateField("type", e.target.value)}
                                            >
                                                <option value="1">All</option>
                                                <option value="2">Cluster</option>
                                                <option value="3">Zone</option>
                                                <option value="4">Site</option>
                                            </select>
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label className="control-label">Cluster</label>
                                            <Select
                                                isClearable
                                                value={selectedCluster}
                                                onChange={searchZone}
                                                options={options}
                                            />
                                        </div>
                                        <div className="form-group col-md-12">
                                            <label className="control-label">Zone</label>
                                            <Select
                                                isClearable
                                                value={selectedZone}
                                                onChange={searchZoneSite}
                                                options={options2}
                                            />
                                        </div>
                                        
                                    </div>

                                    <div className="form-group">
                                        <label className="control-label">Job Name<em className="text-danger">*</em></label>
                                        <input
                                            className="form-control"
                                            required
                                            name="subject"
                                            type="text"
                                            value={form.subject}
                                            onChange={(e) => updateField("subject", e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="control-label">Start Time<em className="text-danger">*</em></label>
                                        <div className="input-group input-group-sm">
                                            <input
                                                type="date"
                                                name="start_date"
                                                className="form-control"
                                                required
                                                value={form.start_date}
                                                onChange={(e) => updateField("start_date", e.target.value)}
                                            />
                                            <input
                                                type="time"
                                                name="start_time"
                                                className="form-control"
                                                required
                                                value={form.start_time}
                                                onChange={(e) => updateField("start_time", e.target.value)}
                                            />
                                        </div>
                                        <br />
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => setIsChecked(e.target.checked)}
                                            />{" "}
                                            Continue <em>(unlimited end time)</em>
                                        </label>
                                    </div>
                                    {!isChecked && (
                                        <div className="form-group">
                                            <label className="control-label">End Time</label>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    type="date"
                                                    name="end_date"
                                                    className="form-control"
                                                    value={form.end_date}
                                                    onChange={(e) => updateField("end_date", e.target.value)}
                                                />
                                                <input
                                                    type="time"
                                                    name="end_time"
                                                    className="form-control"
                                                    value={form.end_time}
                                                    onChange={(e) => updateField("end_time", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="control-label">Organization<em className="text-danger">*</em></label>
                                        <Select
                                            required
                                            isClearable
                                            value={options1.find((o) => String(o.value) === String(organizationId)) ?? null}
                                            onChange={searchUser}
                                            options={options1}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="control-label">User Type<em className="text-danger">*</em></label>
                                        <label className="control-label ms-2">
                                            <input
                                                type="radio"
                                                onChange={checkUserType}
                                                value="1"
                                                name="user_type"
                                                checked={!customUser}
                                            />{" "}
                                            Register User
                                        </label>
                                        <label className="control-label ms-2">
                                            <input
                                                type="radio"
                                                onChange={checkUserType}
                                                value="0"
                                                name="user_type"
                                                checked={customUser}
                                            />{" "}
                                            Custom User
                                        </label>
                                    </div>
                                    {customUser ? (
                                        <div className="form-group">
                                            <label className="control-label">User<em className="text-danger">*</em></label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="user_name"
                                                required
                                                value={form.user_name}
                                                onChange={(e) => updateField("user_name", e.target.value)}
                                            />
                                        </div>
                                    ) : (
                                        <div className="form-group">
                                            <label className="control-label">User<em className="text-danger">*</em></label>
                                            <Select
                                                value={options3.find((o) => String(o.value) === String(selectedUserId)) ?? null}
                                                onChange={ajaxUserInfo}
                                                isClearable
                                                options={options3}
                                            />
                                        </div>
                                    )}
                                    <div className="row">

                                        <div className="form-group  col-md-6">
                                        <label className="control-label">Phone Number<em className="text-danger">*</em></label>
                                        <input
                                            className="form-control"
                                            required
                                            type="text"
                                            name="phone"
                                            value={form.phone}
                                            onChange={(e) => updateField("phone", e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="control-label">Card Number<em className="text-danger">*</em></label>
                                        <input
                                            className="form-control"
                                            required
                                            type="text"
                                            name="card_number"
                                            value={form.card_number}
                                            onChange={(e) => updateField("card_number", e.target.value)}
                                        />
                                    </div>

                                    </div>
                                    <div className="form-group">
                                        <label className="control-label">Job Description<em className="text-danger">*</em></label>
                                        <textarea
                                            placeholder="Write a job description.(Maximum 250 character)"
                                            cols={2}
                                            rows={3}
                                            className="form-control"
                                            maxLength={250}
                                            name="details"
                                            required
                                            value={form.details}
                                            onChange={(e) => updateField("details", e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="form-group col-md-12">
                                            <label className="control-label d-flex justify-content-between">
                                                <span>SITE ID <span className="text-muted">({selectedSiteValues.length} selected)</span></span>
                                                {selectedSiteValues.length > 0 && (
                                                    <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={clearAllSites}>
                                                        Clear All
                                                    </button>
                                                )}
                                            </label>

                                            <div className="card border">
                                                <div className="card-header bg-light p-2 position-relative">
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text"><i className="fa fa-search" /></span>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search selected sites or type to fetch from server..."
                                                            value={siteSearch}
                                                            onChange={(e) => handleSiteSearch(e.target.value)}
                                                        />
                                                        {siteSearch && (
                                                            <button type="button" className="btn btn-outline-secondary" onClick={() => handleSiteSearch("")}>
                                                                <i className="fa fa-times" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {(searchingApi || apiSuggestions.length > 0) && (
                                                        <div
                                                            className="border rounded bg-white shadow-sm position-absolute w-100"
                                                            style={{ left: 0, top: "100%", maxHeight: 220, overflowY: "auto", zIndex: 1050 }}
                                                        >
                                                            {searchingApi && (
                                                                <div className="p-2 text-muted small">Searching...</div>
                                                            )}
                                                            {!searchingApi && apiSuggestions.map((s) => (
                                                                <button
                                                                    type="button"
                                                                    key={s.value}
                                                                    className="btn btn-link w-100 text-start py-1 px-2 text-decoration-none border-bottom"
                                                                    onClick={() => addSite(s)}
                                                                >
                                                                    <i className="fa fa-plus-circle text-success me-2" />{s.label}
                                                                </button>
                                                            ))}
                                                            {!searchingApi && apiSuggestions.length === 0 && siteSearch.trim().length >= 2 && (
                                                                <div className="p-2 text-muted small">No sites found</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="table-responsive" style={{ maxHeight: 260 }}>
                                                    <table className="table table-sm table-hover mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ width: 50 }}>#</th>
                                                                <th>Site ID</th>
                                                                <th style={{ width: 80 }} className="text-center">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {paginatedSites.length === 0 && (
                                                                <tr>
                                                                    <td colSpan={3} className="text-center text-muted py-3">
                                                                        {siteSearch ? "No match in selected sites" : "No sites added yet"}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            {paginatedSites.map((s, idx) => (
                                                                <tr key={s.value}>
                                                                    <td>{(sitePage - 1) * SITE_PAGE_SIZE + idx + 1}</td>
                                                                    <td>{s.label}</td>
                                                                    <td className="text-center">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger py-0 px-2"
                                                                            onClick={() => removeSite(s.value)}
                                                                            title="Remove"
                                                                        >
                                                                            <i className="fa fa-trash" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {totalPages > 1 && (
                                                    <div className="card-footer bg-light p-2 d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            Page {sitePage} of {totalPages} ({filteredSelectedSites.length} sites)
                                                        </small>
                                                        <div className="btn-group btn-group-sm">
                                                            <button type="button" className="btn btn-outline-secondary" disabled={sitePage === 1} onClick={() => setSitePage(1)}>
                                                                <i className="fa fa-angle-double-left" />
                                                            </button>
                                                            <button type="button" className="btn btn-outline-secondary" disabled={sitePage === 1} onClick={() => setSitePage((p) => p - 1)}>
                                                                <i className="fa fa-angle-left" />
                                                            </button>
                                                            <button type="button" className="btn btn-outline-secondary" disabled={sitePage === totalPages} onClick={() => setSitePage((p) => p + 1)}>
                                                                <i className="fa fa-angle-right" />
                                                            </button>
                                                            <button type="button" className="btn btn-outline-secondary" disabled={sitePage === totalPages} onClick={() => setSitePage(totalPages)}>
                                                                <i className="fa fa-angle-double-right" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                            </div>
                            <ModalFooter className="post-footer-comment-icon bg-light">
                                <button type="submit" className="btn btn-success rounded-pill" disabled={loading}>
                                    <i className="fa-light fa-paper-plane-top me-1" />
                                    {isEdit ? "Update" : "Save"}
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
                                    )}
                                </button>
                            </ModalFooter>
                        </form>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
}
